import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ipponApi from "../config/axios";

const BaseURL = import.meta.env.VITE_API_URL;

export type DomainVerificationStatus = "unverified" | "valid" | "invalid";

export interface EmailDomain {
  id: string;
  client_id: string;
  domain: string;
  spf_status: DomainVerificationStatus;
  dkim_status: DomainVerificationStatus;
  dmarc_status: DomainVerificationStatus;
  ses_identity_status: string;
  ses_domain_token: string | null;
  ses_dkim_tokens: string | null;
  last_verified_at: string | null;
  created_at: string;
  updated_at: string | null;
}

// メールドメイン一覧取得
export const fetchEmailDomains = createAsyncThunk<EmailDomain[], string>(
  "emailDomain/fetchAll",
  async (clientId, { rejectWithValue }) => {
    try {
      const response = await ipponApi.get(
        `${BaseURL}/api/v1/clients/${clientId}/email-domains`
      );
      return response.data.data as EmailDomain[];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "メールドメイン一覧の取得に失敗しました"
      );
    }
  }
);

// メールドメイン追加
export const createEmailDomain = createAsyncThunk<
  EmailDomain,
  { clientId: string; domain: string }
>(
  "emailDomain/create",
  async ({ clientId, domain }, { rejectWithValue }) => {
    try {
      const response = await ipponApi.post(
        `${BaseURL}/api/v1/clients/${clientId}/email-domains`,
        { domain }
      );
      return response.data.data as EmailDomain;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "メールドメインの追加に失敗しました"
      );
    }
  }
);

// メールドメイン削除
export const deleteEmailDomain = createAsyncThunk<
  string,
  { clientId: string; domainId: string }
>(
  "emailDomain/delete",
  async ({ clientId, domainId }, { rejectWithValue }) => {
    try {
      await ipponApi.delete(
        `${BaseURL}/api/v1/clients/${clientId}/email-domains/${domainId}`
      );
      return domainId;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "メールドメインの削除に失敗しました"
      );
    }
  }
);

// DNS設定検証
export const verifyEmailDomain = createAsyncThunk<
  EmailDomain,
  { clientId: string; domainId: string }
>(
  "emailDomain/verify",
  async ({ clientId, domainId }, { rejectWithValue }) => {
    try {
      const response = await ipponApi.post(
        `${BaseURL}/api/v1/clients/${clientId}/email-domains/${domainId}/verify`
      );
      return response.data.data as EmailDomain;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "DNS検証に失敗しました"
      );
    }
  }
);

interface EmailDomainState {
  domains: {
    data: EmailDomain[];
    loading: boolean;
    error: boolean;
  };
  verifying: Record<string, boolean>;
}

const initialState: EmailDomainState = {
  domains: {
    data: [],
    loading: false,
    error: false,
  },
  verifying: {},
};

export const emailDomainSlice = createSlice({
  name: "emailDomain",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // 一覧取得
    builder.addCase(fetchEmailDomains.pending, (state) => {
      state.domains.loading = true;
      state.domains.error = false;
    });
    builder.addCase(fetchEmailDomains.fulfilled, (state, action) => {
      state.domains.data = action.payload;
      state.domains.loading = false;
    });
    builder.addCase(fetchEmailDomains.rejected, (state) => {
      state.domains.loading = false;
      state.domains.error = true;
    });

    // 追加
    builder.addCase(createEmailDomain.fulfilled, (state, action) => {
      state.domains.data.push(action.payload);
    });

    // 削除
    builder.addCase(deleteEmailDomain.fulfilled, (state, action) => {
      state.domains.data = state.domains.data.filter(
        (d) => d.id !== action.payload
      );
    });

    // DNS検証
    builder.addCase(verifyEmailDomain.pending, (state, action) => {
      const domainId = action.meta.arg.domainId;
      state.verifying[domainId] = true;
    });
    builder.addCase(verifyEmailDomain.fulfilled, (state, action) => {
      const domainId = action.meta.arg.domainId;
      state.verifying[domainId] = false;
      const index = state.domains.data.findIndex((d) => d.id === action.payload.id);
      if (index !== -1) {
        state.domains.data[index] = action.payload;
      }
    });
    builder.addCase(verifyEmailDomain.rejected, (state, action) => {
      const domainId = action.meta.arg.domainId;
      state.verifying[domainId] = false;
    });
  },
});

export default emailDomainSlice.reducer;
